import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  InputAdornment, List, ListItem, ListItemSecondaryAction, ListItemText, MenuItem, Paper,
  Select, Stack, Tab, Tabs, TextField, Typography, FormControl, InputLabel, Skeleton,
} from '@mui/material';
import { Add, Delete as DeleteIcon, Edit, SearchRounded } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { Checkbox } from '@mui/material';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import {
  createExpense,
  createExpenseCategory,
  createExpenseType,
  deleteExpense,
  deleteExpenseCategory,
  deleteExpenseType,
  listExpenseCategories,
  listExpenses,
  listExpenseTypes,
  updateExpense,
  updateExpenseCategory,
  updateExpenseType,
  type ExpenseRecord,
} from '../services/api';

type Item = { _id: string; name: string };

type SingleListManagerProps = {
  title: string;
  description: string;
  items: Item[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  placeholder: string;
  emptyLabel: string;
  singularLabel: string;
  successLabel: string;
  loadItems: () => Promise<void>;
  createItem: (name: string) => Promise<any>;
  updateItem: (id: string, name: string) => Promise<any>;
  deleteItem: (id: string) => Promise<any>;
};

const SingleListManager = ({
  title,
  description,
  items,
  loading,
  search,
  onSearchChange,
  placeholder,
  emptyLabel,
  singularLabel,
  successLabel,
  loadItems,
  createItem,
  updateItem,
  deleteItem,
}: SingleListManagerProps) => {
  const [dlgOpen, setDlgOpen] = useState(false);
  const bulk = useBulkSelect();
  const [editing, setEditing] = useState<Item | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.name.toLowerCase().includes(q));
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setError('');
    setDlgOpen(true);
  };

  const openEdit = (item: Item) => {
    setEditing(item);
    setName(item.name);
    setError('');
    setDlgOpen(true);
  };

  const closeDlg = () => setDlgOpen(false);

  const save = async () => {
    const value = name.trim();
    if (!value) return setError('Name is required');
    try {
      if (editing) {
        await updateItem(editing._id, value);
      } else {
        await createItem(value);
      }
      closeDlg();
      await loadItems();
      Swal.fire('Success', `${successLabel} ${editing ? 'updated' : 'added'} successfully`, 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to save';
      setError(msg);
      Swal.fire('Error', msg, 'error');
    }
  };

  const remove = async (id: string) => {
    const result = await Swal.fire({
      title: `Delete ${singularLabel.toLowerCase()}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteItem(id);
      await loadItems();
      Swal.fire('Deleted', `${successLabel} deleted successfully`, 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to delete';
      Swal.fire('Error', msg, 'error');
    }
  };

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" gutterBottom>{title}</Typography>
        <Typography color="text.secondary">{description}</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            Add
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box p={2}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: i === 3 ? 0 : 1.5,
                }}
              >
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton height={14} width="55%" />
                  <Skeleton height={12} width="35%" />
                </Box>
                <Skeleton variant="rounded" width={64} height={32} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box p={3} color="text.secondary">{emptyLabel}</Box>
        ) : (
          <>
          <Box sx={{ px: 2, pt: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <BulkSelectBar bulk={bulk} visibleIds={filtered.map((item) => item._id)} noun="item" nounPlural="items" deleteOne={deleteItem} onDone={loadItems} />
          </Box>
          <List dense>
            {filtered.map((item, idx) => (
              <Box key={item._id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <Checkbox size="small" edge="start" checked={bulk.isSelected(item._id)} onChange={() => bulk.toggle(item._id)} sx={{ mr: 1 }} />
                  <ListItemText primary={item.name} />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEdit(item)} sx={{ mr: 0.5 }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => remove(item._id)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            ))}
          </List>
          </>
        )}
      </Paper>

      <Dialog open={dlgOpen} onClose={closeDlg} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit' : 'Add'} {singularLabel}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            autoFocus
            fullWidth
            placeholder={`e.g., ${singularLabel}`}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDlg} variant="outlined">Cancel</Button>
          <Button onClick={save} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

type ExpenseManagerProps = {
  items: ExpenseRecord[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  expenseTypeOptions: Item[];
  expenseCategoryOptions: Item[];
  loadItems: () => Promise<void>;
};

const ExpenseManager = ({
  items,
  loading,
  search,
  onSearchChange,
  expenseTypeOptions,
  expenseCategoryOptions,
  loadItems,
}: ExpenseManagerProps) => {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRecord | null>(null);
  const [expenseType, setExpenseType] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const bulk = useBulkSelect();
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => (
      item.expenseType.toLowerCase().includes(q) ||
      item.expenseCategory.toLowerCase().includes(q)
    ));
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setExpenseType('');
    setExpenseCategory('');
    setError('');
    setDlgOpen(true);
  };

  const openEdit = (item: ExpenseRecord) => {
    setEditing(item);
    setExpenseType(item.expenseType);
    setExpenseCategory(item.expenseCategory);
    setError('');
    setDlgOpen(true);
  };

  const closeDlg = () => setDlgOpen(false);

  const save = async () => {
    const typeValue = expenseType.trim();
    const categoryValue = expenseCategory.trim();
    if (!typeValue || !categoryValue) return setError('Select an expense type and category');
    try {
      if (editing) {
        await updateExpense(editing._id, { expenseType: typeValue, expenseCategory: categoryValue });
      } else {
        await createExpense({ expenseType: typeValue, expenseCategory: categoryValue });
      }
      closeDlg();
      await loadItems();
      Swal.fire('Success', `Expense ${editing ? 'updated' : 'added'} successfully`, 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to save';
      setError(msg);
      Swal.fire('Error', msg, 'error');
    }
  };

  const remove = async (id: string) => {
    const result = await Swal.fire({
      title: 'Delete expense?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteExpense(id);
      await loadItems();
      Swal.fire('Deleted', 'Expense deleted successfully', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to delete';
      Swal.fire('Error', msg, 'error');
    }
  };

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" gutterBottom>Expenses</Typography>
        <Typography color="text.secondary">
          Create reusable type + category combinations for the same user.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Search expenses…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            Add
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box p={2}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: i === 3 ? 0 : 1.5,
                }}
              >
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton height={14} width="55%" />
                  <Skeleton height={12} width="35%" />
                </Box>
                <Skeleton variant="rounded" width={64} height={32} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box p={3} color="text.secondary">No expenses.</Box>
        ) : (
          <>
          <Box sx={{ px: 2, pt: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <BulkSelectBar bulk={bulk} visibleIds={filtered.map((item) => item._id)} noun="expense" nounPlural="expenses" deleteOne={deleteExpense} onDone={loadItems} />
          </Box>
          <List dense>
            {filtered.map((item, idx) => (
              <Box key={item._id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <Checkbox size="small" edge="start" checked={bulk.isSelected(item._id)} onChange={() => bulk.toggle(item._id)} sx={{ mr: 1 }} />
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={700}>{item.expenseType}</Typography>
                        <Typography color="text.secondary">•</Typography>
                        <Typography>{item.expenseCategory}</Typography>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEdit(item)} sx={{ mr: 0.5 }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => remove(item._id)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            ))}
          </List>
          </>
        )}
      </Paper>

      <Dialog open={dlgOpen} onClose={closeDlg} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit' : 'Add'} Expense</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Expense Type</InputLabel>
              <Select
                label="Expense Type"
                value={expenseType}
                onChange={(e) => {
                  setExpenseType(String(e.target.value));
                  setError('');
                }}
              >
                <MenuItem value=""><em>Select type</em></MenuItem>
                {expenseTypeOptions.map((opt) => (
                  <MenuItem key={opt._id} value={opt.name}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Expense Category</InputLabel>
              <Select
                label="Expense Category"
                value={expenseCategory}
                onChange={(e) => {
                  setExpenseCategory(String(e.target.value));
                  setError('');
                }}
              >
                <MenuItem value=""><em>Select category</em></MenuItem>
                {expenseCategoryOptions.map((opt) => (
                  <MenuItem key={opt._id} value={opt.name}>{opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {error && <Typography color="error">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDlg} variant="outlined">Cancel</Button>
          <Button onClick={save} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default function ManageExpenseFroms() {
  const [tab, setTab] = useState<'types' | 'categories' | 'expenses'>('types');
  const [expenseTypes, setExpenseTypes] = useState<Item[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<Item[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [expenseSearch, setExpenseSearch] = useState('');

  const loadTypes = async () => {
    setLoadingTypes(true);
    try {
      const data = await listExpenseTypes();
      setExpenseTypes(Array.isArray(data) ? data : []);
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const data = await listExpenseCategories();
      setExpenseCategories(Array.isArray(data) ? data : []);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const data = await listExpenses();
      setExpenses(Array.isArray(data) ? data : []);
    } finally {
      setLoadingExpenses(false);
    }
  };

  useEffect(() => {
    void loadTypes();
    void loadCategories();
    void loadExpenses();
  }, []);

  return (
    <Box>
      <Box mb={2}>
        <PageHeader title="Expenses" />
        <Typography color="text.secondary">
          Manage expense types, categories, and the unique type/category combinations.
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, borderRadius: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value as 'types' | 'categories' | 'expenses')}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab value="types" label="Expense Types" />
          <Tab value="categories" label="Expense Categories" />
          <Tab value="expenses" label="Expenses" />
        </Tabs>
      </Paper>

      <Box sx={{ display: tab === 'types' ? 'block' : 'none' }}>
        <SingleListManager
          title="Expense Types"
          description="Create the type values users can pick in the expense form."
          items={expenseTypes}
          loading={loadingTypes}
          search={typeSearch}
          onSearchChange={setTypeSearch}
          placeholder="Search expense types…"
          emptyLabel="No expense types."
          singularLabel="Expense Type"
          successLabel="Expense type"
          loadItems={loadTypes}
          createItem={createExpenseType}
          updateItem={updateExpenseType}
          deleteItem={deleteExpenseType}
        />
      </Box>

      <Box sx={{ display: tab === 'categories' ? 'block' : 'none' }}>
        <SingleListManager
          title="Expense Categories"
          description="Create the category values users can pick in the expense form."
          items={expenseCategories}
          loading={loadingCategories}
          search={categorySearch}
          onSearchChange={setCategorySearch}
          placeholder="Search expense categories…"
          emptyLabel="No expense categories."
          singularLabel="Expense Category"
          successLabel="Expense category"
          loadItems={loadCategories}
          createItem={createExpenseCategory}
          updateItem={updateExpenseCategory}
          deleteItem={deleteExpenseCategory}
        />
      </Box>

      <Box sx={{ display: tab === 'expenses' ? 'block' : 'none' }}>
        <ExpenseManager
          items={expenses}
          loading={loadingExpenses}
          search={expenseSearch}
          onSearchChange={setExpenseSearch}
          expenseTypeOptions={expenseTypes}
          expenseCategoryOptions={expenseCategories}
          loadItems={loadExpenses}
        />
      </Box>
    </Box>
  );
}
